// Отрисовка: какой экран показать по состоянию (старт, лобби, подготовка, «передай телефон», игра, итог), игровой
// экран с анимациями событий, карта крупно, журнал. Экраны — preact-компоненты (board/screens/overlays), diff'ом.
import { h } from 'preact';
import type { CardRef, Game as GameState, Iid, PlayerId } from '../types.ts';
import { player, setupDone, turnOf } from '../engine/state.ts';
import { winnersOf } from '../engine/game.ts';
import { NetClient } from './net.ts';
import { GameScreen, layoutHand, needPid, affordInfo, activationCount, AUTO_END_MS } from './board.ts';
import * as FX from './fx.ts';
import { clearNotices } from './notify.ts';
import { sfx } from './sound.ts';
import { digestEvents } from './events.ts';
import type { FeedItem } from './events.ts';
import type { Mode } from './state.ts';
import { setInstallBannerAllowed } from './install.ts';
import { loadGame, clearSave } from './storage.ts';
import { enqueueGame } from './outbox.ts';
import { StartScreen, connectingV, Lobby, setupWaitV, setupPassV, SetupChoose, setupThirdV, inThirdPhase, PassScreen, EndScreen } from './screens.ts';
import * as OV from './overlays.ts';
import type { ZoomKind } from './overlays.ts';
import { byId } from './dom.ts';
import { st, ui, $app, $modal, $, requireG, hasViewer, gameUi, toast, save, canAct, myPrompt, paint, openModal, closeModal, modalOpen } from './store.ts';
import { act, affordableBuys, advanceSetup } from './flow.ts';
import { scheduleBots, botSetupStep } from './bots.ts';
import { isDragging } from './input.ts';
import { syncPresence, myClientId } from './social.ts';
import { loadRatingChange } from './leaders.ts';
import { loadDailyResult } from './daily.ts';
import { unreadCount } from './inbox.ts';
import { replayInfo } from './replay.ts';

export function render(): void {
  if (isDragging()) {
    st.pendingRender = true;
    return;
  }
  st.pendingRender = false;
  // чем занят клиент — видно всем онлайн; состояние экрана и есть источник правды
  syncPresence();
  // плеер записи партии: стол с панелью плеера, без ботов, передачи телефона и итога
  if (ui.replay) {
    if (st.G) renderGameScreen(st.G);
    return;
  }
  // предложение установить как приложение — только там, где оно не мешает: старт и лобби
  setInstallBannerAllowed(st.net ? !st.G || st.net.room?.status === 'lobby' : ui.screen === 'start');
  if (st.net) {
    renderOnline();
    return;
  }
  if (ui.screen === 'start') {
    renderStart();
    return;
  }
  const g = requireG();
  if (g.phase === 'setup') {
    renderSetup(g);
    return;
  }
  if (g.phase === 'ended') {
    renderEnd(g);
    return;
  }
  const need = needPid(g);
  if (player(g, need).bot) {
    // ход бота: человек смотрит со своего места, телефон не передаётся;
    // мгновенный режим — все ходы разом без перерисовки, старый — по шагам с анимациями
    if (ui.viewer == null || player(g, ui.viewer).bot) ui.viewer = firstHuman(g);
    if (!ui.instantBots) renderGameScreen(g);
    scheduleBots();
    return;
  }
  if (ui.viewer !== need || botsPlayedSince(g, need)) {
    renderPass(g, need);
    return;
  }
  renderGameScreen(g);
}
/** Пока игрок не смотрел, прошли чужие ходы (ботов) — показать ему сводку перед его ходом. Вдвоём с ботом — не нужно:
 *  единственный чужой ход и так виден на столе (анимации событий), сводка только мешала бы */
function botsPlayedSince(g: GameState, need: PlayerId): boolean {
  if (!ui.instantBots || g.players.length <= 2) return false;
  const since = ui.seenSeq[need] ?? 0;
  return g.events.some((e) => e.seq > since && e.type === 'turn' && e.pid !== need);
}
function firstHuman(g: GameState): PlayerId {
  const hp = g.players.find((p) => !p.bot);
  return hp ? hp.id : 0;
}
/** Один раз на устройство: подсказать, что плитки нижней панели нажимаются */
function statsHintOnce(): void {
  try {
    if (localStorage.getItem('krutagidon-stats-hint')) return;
    localStorage.setItem('krutagidon-stats-hint', '1');
  } catch { return; }
  toast('Плитки на нижней панели нажимаются: очки — разбивка, жетоны — свойство и черепа, колода и сброс — их карты', 7000);
}

function paintStart(): void {
  paint(h(StartScreen, { ui, saved: loadGame(), online: NetClient.saved(), presence: st.presence, unread: unreadCount(), myId: myClientId(), leaders: st.leaders, daily: st.daily, bosses: st.bosses, corr: st.corr, push: st.push }));
}
function renderStart(): void {
  const urlCode = new URLSearchParams(location.search).get('room');
  if (urlCode && !ui.joinCode) ui.joinCode = urlCode.toUpperCase();
  paintStart();
  closeModal();
}

/** Лобби показывается, пока комната в лобби (или пока не пришло состояние партии) */
const inLobby = (): boolean => !!st.net?.room && (st.net.room.status === 'lobby' || !st.G);
function paintLobby(): void {
  const net = st.net;
  if (!net?.room) return;
  paint(h(Lobby, { r: net.room, myPid: net.pid, status: ui.netStatus, presence: st.presence, myId: myClientId(), unread: unreadCount() }));
}

/** Перерисовать стартовый экран или лобби из состояния (пришёл снимок присутствия, изменился ящик):
 *  только эти экраны показывают «кто онлайн» и колокольчик; открытое окно не трогается */
export function repaintSocial(): void {
  if (isDragging()) return;
  if (!st.net && ui.screen === 'start') paintStart();
  else if (inLobby()) paintLobby();
  // «передай телефон» и итог тоже несут колокольчик, но полная перерисовка сбросила бы прокрутку ленты —
  // там счётчик догоняет при следующей отрисовке, а живой сигнал — всплывающее уведомление
}

function renderOnline(): void {
  const net = st.net;
  if (!net || !net.room) {
    paint(connectingV());
    return;
  }
  const g = st.G;
  if (inLobby()) {
    paintLobby();
    closeModal();
    return;
  }
  if (!g) return;
  if (g.phase === 'setup') {
    renderSetup(g);
    return;
  }
  if (g.phase === 'ended') {
    renderEnd(g);
    return;
  }
  renderGameScreen(g);
}

// ---------- подготовка ----------
function renderSetup(g: GameState): void {
  const s = ui.setup;
  const p = player(g, s.pid);
  const third = inThirdPhase(g, s.pid);
  const corr = !!st.net?.corr;
  if (st.net && setupDone(p) && !third) {
    paint(setupWaitV(g, corr));
    return;
  }
  if (!st.net && p.bot) {
    botSetupStep(g, s.pid);
    advanceSetup(g, s.pid);
    save();
    render();
    return;
  }
  if (!st.net && s.stage === 'pass') {
    paint(setupPassV(p, third));
    return;
  }
  paint(third ? setupThirdV(g, s, corr) : h(SetupChoose, { G: g, s, corr }));
  closeModal();
}

// ---------- передача телефона (хотсит) с лентой событий ----------
function unseenEvents(g: GameState, pid: PlayerId): FeedItem[] {
  const since = ui.seenSeq[pid] ?? 0;
  return digestEvents(g, g.events.filter((e) => e.seq > since), pid);
}
/** Ленты, у которых уже есть слушатель прокрутки (элемент переживает перерисовки diff'ом — вешать один раз) */
const feedWired = new WeakSet<HTMLElement>();
function renderPass(g: GameState, need: PlayerId): void {
  const evs = unseenEvents(g, need);
  paint(h(PassScreen, { G: g, need, evs, same: ui.viewer === need, unread: unreadCount() }));
  closeModal();
  clearNotices();
  // лента целиком и с начала: игроку важнее всего то, что случилось сразу после его хода; читает и листает сам
  const feed = $('#feed');
  if (!feed) return;
  feed.scrollTop = 0;
  // стрелка-подсказка, пока лента не доскроллена до конца
  const wrap = feed.parentElement;
  const upd = (): void => { wrap?.classList.toggle('more', feed.scrollTop + feed.clientHeight < feed.scrollHeight - 8); };
  if (!feedWired.has(feed)) {
    feed.addEventListener('scroll', upd);
    feedWired.add(feed);
  }
  upd();
}

// ---------- игровой экран ----------
/** Отрисовать игровой экран из состояния (без событий/анимаций): веер раскладывается после diff'а,
 *  превью поднятой карты — часть экрана, появление анимируется */
let lastPreview: Iid | null = null;
function paintGame(g: GameState): void {
  paint(h(GameScreen, { G: g, ui: gameUi(), replay: ui.replay ? replayInfo() : null }));
  layoutHand($app);
  if (ui.raised !== lastPreview) {
    lastPreview = ui.raised;
    if (ui.raised != null) FX.popIn($('#preview .preview-card'), { duration: 220 });
  }
}
function renderGameScreen(g: GameState): void {
  const gu = gameUi();
  // состояние экрана до отрисовки: вкладка по ходу, автозавершение, поднятая карта ещё на руке?
  autoMode(g);
  updateAutoEnd(g);
  if (ui.raised != null && !findInHand(ui.raised)) ui.raised = null;
  const before = FX.capture($app);
  paintGame(g);
  const after = FX.capture($app);
  FX.flip(before, after);
  // события → анимации и звуки
  const evs = g.events.filter((e) => e.seq > ui.lastSeq);
  const last = evs[evs.length - 1];
  if (last) {
    if (ui.lastSeq > 0 && evs.length <= 40) {
      const ctx: FX.FxCtx = {
        me: gu.viewer,
        before,
        after,
        avatar: (pid) => $(`[data-fx="p:${pid}"]`),
        bar: (pid) => $(`[data-fx="hp:${pid}"]`),
        power: $('[data-fx="power"]'),
        deckEl: (pid) => $(`[data-fx="deck:${pid}"]`) ?? $(`[data-fx="p:${pid}"]`),
        discardEl: (pid) => $(`[data-fx="discard:${pid}"]`),
        trashEl: $('[data-fx="trash"]'),
        playerName: (pid) => player(g, pid).name,
      };
      FX.playEvents(evs, ctx);
    }
    ui.lastSeq = g.eventSeq || last.seq;
  }
  if (!st.net && !ui.replay) ui.seenSeq[gu.viewer] = g.eventSeq;
  statsHintOnce();
  if (ui.zoom) renderZoom(g);
  if (!myPrompt()) ui.sheetCollapsed = false;
}
// Всё сыграно, мощь потрачена, купить нечего — полоска под «Конец хода» (рисует GameScreen из ui.autoEndAt) и автозавершение
function updateAutoEnd(g: GameState): void {
  const me = ui.viewer != null ? g.players[ui.viewer] : undefined;
  // неприменённое свойство ☀ — это ещё не конец хода
  const idle = canAct() && me && !me.hand.length && turnOf(g).power === 0 && !affordableBuys(g).length && !activationCount(g, me) && !modalOpen();
  if (!idle) {
    if (ui.autoEndTimer) clearTimeout(ui.autoEndTimer);
    ui.autoEndTimer = null;
    ui.autoEndAt = null;
    return;
  }
  if (!ui.autoEndAt) {
    ui.autoEndAt = Date.now() + AUTO_END_MS;
    ui.autoEndTimer = setTimeout(() => {
      ui.autoEndTimer = null;
      ui.autoEndAt = null;
      if (canAct() && !modalOpen()) act({ name: 'endTurn', args: [] });
    }, AUTO_END_MS);
  }
}
// Новый ход — стол; все карты с руки сыграны — барахолка (один раз за ход)
function autoMode(g: GameState): void {
  if (g.phase !== 'play') return;
  const turnEv = g.events.filter((e) => e.type === 'turn').pop();
  const turnSeq = turnEv ? turnEv.seq : 0;
  if (ui.turnSeq !== turnSeq) {
    ui.turnSeq = turnSeq;
    ui.autoBuyDone = false;
    if (ui.mode !== 'play') setMode('play', false);
  }
  const me = ui.viewer != null ? g.players[ui.viewer] : undefined;
  if (canAct() && me && !me.hand.length && !ui.autoBuyDone) {
    // пока на столе есть неприменённые свойства ☀ — остаёмся на столе (значок на вкладке напоминает);
    // переход на барахолку — только когда всё разыграно и применено, и только если есть что купить
    if (activationCount(g, me) > 0) return;
    ui.autoBuyDone = true;
    if (affordableBuys(g).length > 0) setMode('buy', false);
  }
}
export function findInHand(iid: Iid): CardRef | null {
  const me = st.G && ui.viewer != null ? st.G.players[ui.viewer] : undefined;
  return me?.hand.find((c) => c.iid === iid) ?? null;
}
/** Перерисовать игровой экран после изменения ui (поднятая карта, вкладка, выбор в запросе) — без событий и анимаций */
export function repaintGame(): void {
  if (st.G && st.G.phase === 'play' && hasViewer(ui) && $('.game')) paintGame(st.G);
}
export function setRaised(iid: Iid | null): void {
  ui.raised = iid == null || ui.raised === iid ? null : iid;
  repaintGame();
  if (ui.raised != null) sfx('select');
}
export function unraise(): void {
  if (ui.raised == null) return;
  ui.raised = null;
  repaintGame();
}
/** paint=false — только состояние (из autoMode перед общей отрисовкой, чтобы не сбить снимок анимаций) */
export function setMode(mode: Mode, paint = true): void {
  if (ui.mode === mode) return;
  ui.mode = mode;
  ui.raised = null;
  if (paint) repaintGame();
  sfx('select');
}

// ---------- карта крупно, журнал ----------
/** Открыть карту крупно; окно перерисовывается из ui.zoom (степпер чипсин меняет состояние, а не DOM) */
export function showZoom(g: GameState, c: CardRef, kind: ZoomKind): void {
  let useChips = 0;
  if (kind === 'market' && canAct()) {
    const turn = turnOf(g);
    const { price, chipPay } = affordInfo(g, player(g, turn.player), c);
    useChips = Math.max(0, Math.min(chipPay, price - turn.power));
  }
  ui.zoom = { card: c, kind, useChips };
  ui.zoomOrig = false;
  renderZoom(g);
  FX.popIn($modal.querySelector('.dialog'), { duration: 200 });
}
export function renderZoom(g: GameState): void {
  const z = ui.zoom;
  if (!z) return;
  openModal(() => OV.zoomV(g, z.card, z.kind, { isActive: canAct(), useChips: z.useChips, orig: ui.zoomOrig }));
}
export function closeZoom(): void {
  const back = ui.zoomBack;
  ui.zoom = null;
  ui.zoomOrig = false;
  ui.zoomBack = null;
  closeModal();
  if (back) back();
}
export function showLog(): void {
  if (!st.G) return;
  openModal(() => OV.logV(requireG(), ui.viewer));
  const box = byId('logbox');
  if (box) box.scrollTop = box.scrollHeight;
}

/** Экран итога без побочных эффектов: им же перерисовывается пришедшая плашка рейтинга (окна не трогаются) */
function paintEnd(g: GameState): void {
  const rating = g.seed != null && st.rating.seed === g.seed ? st.rating : null;
  paint(h(EndScreen, { G: g, online: st.net ? (st.net.corr ? 'corr' : true) : false, unread: unreadCount(), rating: rating?.data ?? null, ratingLoading: !!rating?.loading, daily: st.daily }));
}
function renderEnd(g: GameState): void {
  paintEnd(g);
  closeModal();
  clearNotices();
  // готовая хотсит-партия — в очередь на отправку для разбора (онлайн-партии записывает сервер)
  if (!st.net) enqueueGame(g);
  // как партия сдвинула место в рейтинге — плашкой на этом же экране, когда ответит сервер
  if (g.seed != null && st.rating.seed !== g.seed && g.players.some((p) => !p.bot)) void loadRatingChange(g.seed, () => { if (st.G === g && g.phase === 'ended' && !isDragging()) paintEnd(g); });
  // партия дня: запись ушла — место в таблице дня на этом же экране
  if (!st.net && g.daily) void loadDailyResult(g.daily, () => { if (st.G === g && g.phase === 'ended' && !isDragging()) paintEnd(g); });
  if (!ui.endShown) {
    ui.endShown = true;
    sfx('win');
    const ws = winnersOf(g).map((id) => player(g, id).name);
    const who = ws.length > 1 ? `${ws.slice(0, -1).join(', ')} и ${ws[ws.length - 1]} побеждают!` : `${ws[0] ?? ''} побеждает!`;
    FX.splash(`<div class="sp-big">🏆</div><div class="sp-sub">${who}</div>`, 'sp-win', { duration: 2200 });
  }
  if (!st.net) clearSave();
}
